import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight } from "lucide-react";
import Interactive3DPhone from "@/components/Interactive3DPhone";
import { api } from "@/lib/api";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

export default function Hero({ onOrderClick }) {
  const ref = useRef(null);
  const [content, setContent] = useState({
    hero_badge: "iPhone Spezialist · über 10 Jahre Erfahrung",
    hero_subtitle: "Über 10 Jahre Erfahrung mit Apple-Reparaturen: faire Festpreise, Express-Bearbeitung meist an 1 Werktag, deine Daten bleiben zu 100% erhalten. Einschicken, reparieren lassen, versichert zurückbekommen – deutschlandweit.",
  });
  useEffect(() => {
    api.get("/content").then((r) => r.data && setContent((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#050505]"
    >
      {/* ambient background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_40%,rgba(255,255,255,0.07),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_90%,rgba(255,255,255,0.04),transparent_50%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-32 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left: kinetic copy */}
        <motion.div style={{ y: textY, willChange: "transform" }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex items-center gap-3 mb-8 font-mono text-xs uppercase tracking-[0.25em] text-[#888]"
          >
            <span className="w-8 h-px bg-[#555]" />
            {content.hero_badge}
          </motion.div>

          <h1 className="font-display text-white leading-[1.05] text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight">
            {["Express,", "günstig,", "deutschlandweit."].map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="text-[#a5a5a5] text-base sm:text-lg max-w-md leading-relaxed mt-8"
          >
            {content.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex items-center gap-6"
          >
            <button
              type="button"
              onClick={onOrderClick}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-white text-[#050505] pl-7 pr-2 py-2 text-base font-medium hover:bg-[#d1d5db] transition-colors duration-300"
            >
              Reparatur beauftragen
              <span className="w-11 h-11 rounded-full bg-[#050505] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={18} />
              </span>
            </button>
            <Link
              to="/preisliste"
              data-testid="hero-pricing-link"
              className="text-sm text-[#a5a5a5] hover:text-white transition-colors duration-300"
            >
              Preise ansehen
            </Link>
          </motion.div>
        </motion.div>

        {/* Right: interactive 3D iPhone */}
        <motion.div style={{ y: phoneY, willChange: "transform" }} className="h-[520px] sm:h-[600px] lg:h-[640px]">
          <Interactive3DPhone />
        </motion.div>
      </div>
    </section>
  );
}
